"use client";

/**
 * ─── PLAN GATE (Archivo puente / validador) ────────────────────
 *
 * Componente intermedio que valida si el usuario tiene acceso
 * al contenido de la página actual según su plan.
 *
 * - NO oculta páginas del sidebar (todas se muestran siempre).
 * - SI bloquea el CONTENIDO de la página mostrando un wall de upgrade.
 * - Funciona por pathname: lee la ruta actual, consulta qué features
 *   requiere, y ejecuta el cascade de 4 capas para verificar.
 * - Aunque alguien navegue directo por URL, el contenido no se muestra.
 *
 * Uso: Se monta en AppShell y envuelve {children} automáticamente.
 * ────────────────────────────────────────────────────────────────
 */

import React, { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { supabase } from "@/lib/supabase";
import { getFeaturesForPath, type FeatureCode } from "@/lib/auth/feature-map";

type FeatureMap = Record<string, boolean>;

type GateState = {
  status: "loading" | "allowed" | "blocked";
  missingFeatures: string[];
  planName: string | null;
};

/** Paths that never need a plan gate (always accessible) */
const ALWAYS_ALLOWED = [
  "/",
  "/dashboard",
  "/auth",
  "/onboarding",
  "/platform",
];

/**
 * Resolves the full 4-layer entitlements cascade client-side.
 * Returns the feature map + current plan name.
 */
async function resolveClientEntitlements(): Promise<{
  features: FeatureMap;
  planName: string | null;
}> {
  try {
    const { data: userRes } = await supabase.auth.getUser();
    const userId = userRes?.user?.id;
    if (!userId) return { features: {}, planName: null };

    // Profile → tenant_id + role_id
    const { data: profileRow } = await supabase
      .from("profiles")
      .select("tenant_id, id, role_id")
      .eq("user_id", userId)
      .maybeSingle();

    const tenantId = profileRow?.tenant_id ? String(profileRow.tenant_id) : null;
    const roleId = profileRow?.role_id ? String(profileRow.role_id) : null;
    if (!tenantId) return { features: {}, planName: null };

    // ── Cache DESHABILITADO para evitar bugs ──
    // El caché causaba que los cambios de plan no se reflejaran inmediatamente

    // Load feature id→code
    const { data: featureRows } = await supabase
      .from("features")
      .select("id, code");

    if (!featureRows?.length) return { features: {}, planName: null };

    const idToCode = new Map<string, string>();
    const allCodes = new Set<string>();
    for (const r of featureRows) {
      const id = String(r.id ?? "");
      const code = String(r.code ?? "").trim();
      if (id && code) {
        idToCode.set(id, code);
        allCodes.add(code);
      }
    }

    // Layer 1: Plan features
    const planEnabled = new Set<string>();
    let hasPlan = false;
    let planName: string | null = null;

    const { data: tpRow } = await supabase
      .from("tenant_plans")
      .select("plan_id, is_active, expires_at")
      .eq("tenant_id", tenantId)
      .eq("is_active", true)
      .limit(1)
      .maybeSingle();

    if (tpRow?.plan_id) {
      hasPlan = true;

      // Get plan name
      const { data: planRow } = await supabase
        .from("plans")
        .select("name")
        .eq("id", tpRow.plan_id)
        .maybeSingle();

      planName = planRow?.name ?? null;

      const { data: pfRows } = await supabase
        .from("plan_features")
        .select("feature_id, is_enabled")
        .eq("plan_id", tpRow.plan_id);

      if (Array.isArray(pfRows)) {
        for (const row of pfRows) {
          if (row.is_enabled !== false) {
            const code = idToCode.get(String(row.feature_id));
            if (code) planEnabled.add(code);
          }
        }
      }
    }

    // Layer 2: Role permissions
    const roleAllowed = new Map<string, boolean>();
    let hasRolePerms = false;

    if (roleId) {
      const { data: rfpRows } = await supabase
        .from("role_feature_permissions")
        .select("feature_id, is_allowed")
        .eq("role_id", roleId);

      if (Array.isArray(rfpRows) && rfpRows.length > 0) {
        hasRolePerms = true;
        for (const row of rfpRows) {
          const code = idToCode.get(String(row.feature_id));
          if (code) roleAllowed.set(code, Boolean(row.is_allowed));
        }
      }
    }

    // Layer 3: Tenant overrides
    const tenantOverride = new Map<string, boolean>();
    {
      const { data: ovRows } = await supabase
        .from("tenant_feature_overrides")
        .select("feature_id, is_enabled")
        .eq("tenant_id", tenantId);

      if (Array.isArray(ovRows)) {
        for (const row of ovRows) {
          const code = idToCode.get(String(row.feature_id));
          if (code) tenantOverride.set(code, Boolean(row.is_enabled));
        }
      }
    }

    // Layer 4: User overrides
    const userOverride = new Map<string, boolean>();
    {
      const { data: upRows } = await supabase
        .from("user_feature_permissions")
        .select("feature_id, is_allowed")
        .eq("user_id", userId);

      if (Array.isArray(upRows)) {
        for (const row of upRows) {
          const code = idToCode.get(String(row.feature_id));
          if (code) userOverride.set(code, Boolean(row.is_allowed));
        }
      }
    }

    // Merge cascade
    const result: FeatureMap = {};

    // ── Free plan features (fallback cuando no hay plan) ──
    const FREE_FEATURES = new Set(["pos.products", "pos.sales"]);

    for (const code of allCodes) {
      // Si NO hay plan → solo Free features
      // Si SÍ hay plan → debe estar en plan_features
      let allowed = hasPlan ? planEnabled.has(code) : FREE_FEATURES.has(code);
      if (hasRolePerms) allowed = allowed && (roleAllowed.get(code) === true);
      if (tenantOverride.has(code)) allowed = tenantOverride.get(code)!;
      if (userOverride.has(code)) allowed = userOverride.get(code)!;
      result[code] = allowed;
    }

    // ── Cache DESHABILITADO ──

    return { features: result, planName };
  } catch {
    return { features: {}, planName: null };
  }
}

/** Human-readable names for features */
const FEATURE_LABELS: Record<string, string> = {
  "pos.sales": "Ventas",
  "pos.products": "Productos",
  "pos.inventory": "Inventario",
  "pos.users": "Usuarios",
  cash: "Caja",
  warehouse_basic: "Almacén",
  warehouse_counts: "Conteos",
  warehouse_transfers: "Transferencias",
  settings: "Configuración",
  reports: "Reportes",
  quotes: "Cotizaciones",
  quotes_basic: "Cotizaciones básico",
  quotes_convert: "Cotizaciones → Venta",
  quotes_crm: "Cotizaciones CRM",
  suppliers: "Proveedores",
};

function UpgradeWall({
  missingFeatures,
  planName,
}: {
  missingFeatures: string[];
  planName: string | null;
}) {
  return (
    <div className="flex flex-1 items-center justify-center min-h-[60vh] px-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Lock icon */}
        <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 flex items-center justify-center">
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            className="text-orange-400"
          >
            <rect
              x="5"
              y="11"
              width="14"
              height="10"
              rx="2"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M8 11V7a4 4 0 118 0v4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="12" cy="16" r="1.5" fill="currentColor" />
          </svg>
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Módulo no disponible
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Tu plan actual{" "}
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              {planName || "Free"}
            </span>{" "}
            no incluye acceso a{" "}
            {missingFeatures
              .map((f) => FEATURE_LABELS[f] || f)
              .join(", ")}
            .
          </p>
        </div>

        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200/50 dark:border-blue-800/40 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
            Actualiza tu plan para desbloquear esta funcionalidad
          </p>
          <div className="text-xs text-blue-600/70 dark:text-blue-400/60">
            Contacta a tu administrador o actualiza desde la configuración de tu
            cuenta.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PlanGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [gate, setGate] = useState<GateState>({
    status: "loading",
    missingFeatures: [],
    planName: null,
  });

  // Check if the current path is always allowed
  const isAlwaysAllowed = useMemo(() => {
    const normalized = (pathname || "/").replace(/\/+$/, "") || "/";
    return ALWAYS_ALLOWED.some(
      (p) => normalized === p || normalized.startsWith(p + "/")
    );
  }, [pathname]);

  useEffect(() => {
    if (isAlwaysAllowed) {
      setGate({ status: "allowed", missingFeatures: [], planName: null });
      return;
    }

    // ── CRÍTICO: Resetear a "loading" cuando cambia el pathname ──
    // Sin esto, al navegar de /venta (allowed) a /caja (blocked),
    // el estado viejo "allowed" persiste mientras carga el check
    // y el contenido se alcanza a ver brevemente.
    setGate({ status: "loading", missingFeatures: [], planName: null });

    let cancelled = false;

    (async () => {
      // What features does this route need?
      const requiredFeatures = getFeaturesForPath(pathname || "/");

      // No feature requirement → allowed
      if (!requiredFeatures || requiredFeatures.length === 0) {
        if (!cancelled) {
          setGate({ status: "allowed", missingFeatures: [], planName: null });
        }
        return;
      }

      // Resolve entitlements
      const { features, planName } = await resolveClientEntitlements();

      if (cancelled) return;

      // Check: ALL required features must be true (AND logic)
      const missing = requiredFeatures.filter((f) => features[f] !== true);

      if (missing.length === 0) {
        setGate({ status: "allowed", missingFeatures: [], planName });
      } else {
        setGate({ status: "blocked", missingFeatures: missing, planName });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pathname, isAlwaysAllowed]);

  // Loading state — show a subtle skeleton, not a full-page block
  if (gate.status === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center min-h-[40vh]">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-gray-600 dark:border-t-purple-400" />
      </div>
    );
  }

  // Blocked — show the upgrade wall instead of page content
  if (gate.status === "blocked") {
    return (
      <UpgradeWall
        missingFeatures={gate.missingFeatures}
        planName={gate.planName}
      />
    );
  }

  // Allowed — render page content normally
  return <>{children}</>;
}
